import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { useCreditsAccount } from "../../components/Credits/AddToListing";
import ImageUploader from "../../components/ImageUploader/ImageUploader";
import { ImportProgressPanel } from "../../components/ListingImportProgress/ListingImportProgress";
import NewNavbar from "../../components/Navbar/NewNavbar";
import VideoPreviewCard from "../../components/VideoPreviewCard/VideoPreviewCard";
import { API_URL } from "../../constants";
import useAuthenticatedVideoBlob from "../../hooks/useAuthenticatedVideoBlob";
import useIntakeCooldown from "../../hooks/useIntakeCooldown";
import useVideoPreview, { approvedFinalVideo } from "../../hooks/useVideoPreview";
import useZillowImport from "../../hooks/useZillowImport";
import { authHeader, getToken } from "../../utils/auth";
import { captureIntakeFailure, intakeErrorText } from "../../utils/intakeLimits";
import { absoluteVideoUrl } from "../../utils/videoPreview";
import {
  defaultVideoMode,
  emptyVideoSelection,
  videoPhotosFromDocument,
} from "../../utils/videoShotPlan";

const Page = styled.main`
  max-width: 1020px; margin: 0 auto; padding: 120px 20px 60px;
  h1 { font-size: clamp(1.8rem, 5vw, 2.6rem); }
  h2 { font-size: 1.35rem; }
  p { line-height: 1.6; }
  button, input, select { font: inherit; max-width: 100%; }
  button { padding: 10px 16px; border: 1px solid #bdd4cf; border-radius: 8px; background: #f1f8f6; color: #16463c; margin: 6px 8px 6px 0; }
  button:disabled { opacity: 0.55; cursor: not-allowed; }
  input:not([type="checkbox"]):not([type="file"]), select { display: block; padding: 10px; margin: 6px 0 16px; width: 100%; }
  section { margin: 24px 0; }
`;
const Panel = styled.section`border: 1px solid #dce7e4; border-radius: 14px; padding: 22px;`;
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

/** @param {any} error */
const requestError = (error) =>
  intakeErrorText(captureIntakeFailure(error)) ||
  (typeof error?.response?.data?.error === "string"
    ? error.response.data.error.slice(0, 400)
    : "We could not finish this request. Your saved photos are kept. Check the current draft before trying again.");

/** @param {any} value */
export const validVideoSource = (value) =>
  !!value &&
  UUID.test(value.uuid) &&
  UUID.test(value.computation_uuid) &&
  value.source_kind === "photos" &&
  Number.isSafeInteger(value.version) &&
  value.version > 0 &&
  !!value.photo_manifest &&
  typeof value.photo_manifest === "object" &&
  !Array.isArray(value.photo_manifest) &&
  Object.entries(value.photo_manifest).every(
    ([name, url]) =>
      /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(name) && typeof url === "string" && !!url
  );

/** Mounted only after the owner explicitly chooses the optional import path. */
const OptionalZillow = () => {
  const history = useHistory();
  const [url, setUrl] = useState("");
  const importer = useZillowImport({
    resumePending: true,
    onReady: (listing) => {
      if (UUID.test(listing?.uuid)) history.push(`/create-video/${listing.uuid}`);
    },
  });
  return (
    <Panel>
      <h2>Use a Zillow listing (optional)</h2>
      <p>
        Import available photos and details, then choose your video shots. This does not prepare a
        report.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void importer.start(url);
        }}
      >
        <label htmlFor="video-zillow-url">Zillow listing URL</label>
        <input
          id="video-zillow-url"
          type="url"
          required
          value={url}
          disabled={importer.busy}
          onChange={(event) => setUrl(event.target.value)}
        />
        <button
          type="submit"
          disabled={importer.busy || importer.cooldown.blocked || !!importer.uuid}
        >
          Import photos
        </button>
      </form>
      {(importer.visible || importer.error) && <ImportProgressPanel importer={importer} />}
    </Panel>
  );
};

const StartVideo = () => {
  const history = useHistory();
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [listings, setListings] = useState(/** @type {any[]} */ ([]));
  const [listingId, setListingId] = useState("");
  const [zillow, setZillow] = useState(false);
  const cooldown = useIntakeCooldown();
  const alive = useRef(false);
  const inFlight = useRef(false);
  useEffect(() => {
    alive.current = true;
    const token = getToken();
    axios
      .get(`${API_URL}/auth/me/reports/`, { headers: authHeader(), timeout: 15000 })
      .then(({ data }) => {
        if (!alive.current || getToken() !== token) return;
        const unique = new Map();
        for (const row of Array.isArray(data?.reports) ? data.reports : []) {
          if (UUID.test(row.listing_uuid)) unique.set(row.listing_uuid, row);
        }
        setListings([...unique.values()]);
      })
      .catch(() => {});
    return () => {
      alive.current = false;
    };
  }, []);
  const create = async () => {
    if (inFlight.current || cooldown.blocked) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    const token = getToken();
    try {
      const { data } = await axios.post(
        `${API_URL}/listings/create-video/`,
        { title: title.trim(), ...(notes.trim() ? { notes: notes.trim() } : {}) },
        { headers: authHeader(), timeout: 30000 }
      );
      if (!alive.current || getToken() !== token) return;
      if (!UUID.test(data?.uuid)) throw new Error("Incomplete listing response");
      history.push(`/create-video/${data.uuid}`);
    } catch (failure) {
      if (alive.current && getToken() === token) setError(requestError(failure));
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  };
  return (
    <>
      <Panel>
        <h2>Start with your photos</h2>
        <p>
          No Zillow link, property facts, or listing analysis is required. Upload photos, select up
          to six shots, and confirm the video cost before generation.
        </p>
        <label htmlFor="video-title">Listing label (optional)</label>
        <input
          id="video-title"
          maxLength={200}
          value={title}
          disabled={busy}
          onChange={(event) => setTitle(event.target.value)}
        />
        <label htmlFor="video-notes">Listing notes (optional)</label>
        <textarea
          id="video-notes"
          rows={3}
          maxLength={4000}
          value={notes}
          disabled={busy}
          onChange={(event) => setNotes(event.target.value)}
        />
        <p>
          Notes are saved with the listing. They do not start research or add claims to the video.
        </p>
        <button type="button" disabled={busy || cooldown.blocked} onClick={create}>
          {busy ? "Creating photo draft…" : "Start a photo video"}
        </button>
        {error && <p role="alert">{error}</p>}
      </Panel>
      <Panel>
        <h2>Use an existing listing</h2>
        <label htmlFor="video-existing-listing">Your listing</label>
        <select
          id="video-existing-listing"
          value={listingId}
          disabled={busy}
          onChange={(event) => setListingId(event.target.value)}
        >
          <option value="">Choose a listing</option>
          {listings.map((listing) => (
            <option key={listing.listing_uuid} value={listing.listing_uuid}>
              {listing.address || "Untitled listing"}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy || !listingId}
          onClick={() => history.push(`/create-video/${listingId}`)}
        >
          Use this listing’s photos
        </button>
        <p>You can also choose exact video photos from an existing listing’s results page.</p>
      </Panel>
      <button
        type="button"
        disabled={busy}
        onClick={() => setZillow(!zillow)}
        aria-expanded={zillow}
      >
        Use a Zillow link instead (optional)
      </button>
      {zillow && <OptionalZillow />}
    </>
  );
};

/** @param {{source:any,listingUuid:string,onBusy:(busy:boolean)=>void,
 * selection:import('../../utils/videoShotPlan').VideoShotSelection,
 * onSelectionChange:(value:import('../../utils/videoShotPlan').VideoShotSelection)=>void}} props */
const SourceVideo = ({ source, listingUuid, onBusy, selection, onSelectionChange }) => {
  const video = useVideoPreview({ uuid: source.computation_uuid, active: true });
  const account = useCreditsAccount();
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const downloadGuard = useRef(false);
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const final = video.preview?.final_video;
  const playback = useAuthenticatedVideoBlob({
    sourceUrl: final?.available === true ? final.play_url : "",
    active: true,
    cacheKey: `${source.uuid}:${video.preview?.id || ""}`,
  });
  const locked =
    !["ready", "disabled"].includes(video.phase) ||
    !!video.action ||
    video.startPending ||
    [
      "queued",
      "requested",
      "selecting",
      "generating",
      "reviewing",
      "stitching",
      "processing",
    ].includes(video.preview?.status) ||
    downloading;
  useEffect(() => {
    onBusy(locked);
    return () => onBusy(true);
  }, [locked, onBusy]);
  const download = async () => {
    if (downloadGuard.current) return;
    downloadGuard.current = true;
    setDownloading(true);
    setDownloadError("");
    const token = getToken();
    try {
      const refreshed = await video.refresh();
      const approved = approvedFinalVideo(refreshed?.preview);
      if (!alive.current || token !== getToken()) return;
      if (!approved) throw new Error("Approval is required");
      const response = await axios.get(
        absoluteVideoUrl(approved.download_url || approved.play_url),
        { headers: authHeader(), responseType: "blob", timeout: 30000 }
      );
      if (!alive.current || token !== getToken()) return;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "listing-video.mp4";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      if (alive.current && token === getToken())
        setDownloadError(
          "The approved video could not be downloaded. Check its status and try again."
        );
    } finally {
      downloadGuard.current = false;
      if (alive.current) setDownloading(false);
    }
  };
  return (
    <VideoPreviewCard
      {...video}
      enabled={video.enabled && !!video.credits}
      reportLinked={false}
      shotSelection={selection}
      onShotSelectionChange={onSelectionChange}
      listingUuid={listingUuid}
      photos={videoPhotosFromDocument(source)}
      sourceVersion={source.version}
      allowAutomatic={false}
      balance={account.credits?.balance ?? null}
      playbackUrl={playback.blobUrl}
      playbackLoading={playback.loading}
      playbackError={playback.error}
      externalError={downloadError}
      externalBusy={downloading ? "download" : null}
      onGenerate={(count, options) => video.generate(count, source.version, options || null)}
      onCheckStartStatus={video.checkStartStatus}
      onDismissCreditsBlock={video.dismissCreditsBlock}
      onApprove={video.approve}
      onReject={video.reject}
      onRetry={video.refresh}
      onDownload={download}
    />
  );
};

/** @param {{listingUuid:string}} props */
const VideoDraft = ({ listingUuid }) => {
  const [listing, setListing] = useState(/** @type {any} */ (null));
  const [source, setSource] = useState(/** @type {any} */ (null));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(true);
  const [changed, setChanged] = useState(false);
  // AI motion where suitable until the video endpoint says otherwise: the card
  // moves an untouched draft to photo motion when ai_enabled arrives false. The
  // selection lives here, above the per-source card, so an owner's own choice
  // survives a source update that remounts the card.
  const [selection, setSelection] = useState(() =>
    emptyVideoSelection(defaultVideoMode({ aiEnabled: true, allowAutomatic: false }))
  );
  const [error, setError] = useState("");
  const guard = useRef(false);
  const alive = useRef(false);
  const tokenRef = useRef(getToken());
  const current = useCallback(() => alive.current && getToken() === tokenRef.current, []);
  const load = useCallback(async () => {
    try {
      const [details, snapshot] = await Promise.all([
        axios.get(`${API_URL}/listings/${listingUuid}/`, { headers: authHeader(), timeout: 15000 }),
        axios.get(`${API_URL}/listings/${listingUuid}/video-source/`, {
          headers: authHeader(),
          timeout: 15000,
        }),
      ]);
      if (!current()) return;
      if (
        details.data?.uuid !== listingUuid ||
        (snapshot.data?.source !== null && !validVideoSource(snapshot.data?.source))
      )
        throw new Error("Incomplete photo source");
      setListing(details.data);
      setSource(snapshot.data.source);
      if (!snapshot.data.source) setVideoBusy(false);
      setError("");
    } catch (failure) {
      if (current()) setError(requestError(failure));
    } finally {
      if (current()) setLoading(false);
    }
  }, [listingUuid, current]);
  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);
  const mutate = async (/** @type {()=>Promise<any>} */ operation) => {
    if (guard.current || videoBusy || !current()) return null;
    guard.current = true;
    setBusy(true);
    setError("");
    try {
      return await operation();
    } catch (failure) {
      if (current()) setError(requestError(failure));
      throw failure;
    } finally {
      guard.current = false;
      if (current()) setBusy(false);
    }
  };
  const refreshPhotos = async () => {
    const { data } = await axios.get(`${API_URL}/listings/${listingUuid}/`, {
      headers: authHeader(),
      timeout: 15000,
    });
    if (current() && data?.uuid === listingUuid) {
      setListing(data);
      setChanged(true);
    }
  };
  const upload = (/** @type {File[]} */ files) =>
    mutate(async () => {
      const form = new FormData();
      for (const file of files) form.append("images", file);
      await axios.post(`${API_URL}/listings/${listingUuid}/video-source/images/`, form, {
        headers: authHeader(),
        timeout: 30000,
      });
      await refreshPhotos();
      return { ok: true };
    });
  const snapshot = () => {
    void mutate(async () => {
      const { data } = await axios.post(
        `${API_URL}/listings/${listingUuid}/video-source/`,
        { expected_version: source?.version || 0 },
        { headers: authHeader(), timeout: 30000 }
      );
      if (!current()) return;
      if (!validVideoSource(data?.source)) throw new Error("Incomplete photo source");
      if (data.source.uuid !== source?.uuid || data.source.version !== source?.version)
        setVideoBusy(true);
      setSource(data.source);
      setChanged(false);
    }).catch(() => {});
  };
  if (loading) return <output>Loading your video photos…</output>;
  if (!listing)
    return (
      <>
        <p role="alert">{error}</p>
        <button type="button" onClick={load}>
          Check current draft
        </button>
      </>
    );
  return (
    <>
      <Panel>
        <h2>{listing.address || "Your photo draft"}</h2>
        <p>
          Upload source photos here. To replace a shot, upload its replacement, update the video
          photos, then choose the new image in your shot plan. Prior source photos are kept;
          existing reports are unchanged. Updating the source creates a new video version that needs
          its own review.
        </p>
        <ImageUploader
          images={listing.images || []}
          listingUuid={listingUuid}
          onUpload={upload}
          onDelete={() => {}}
          allowDelete={false}
          uploading={busy || videoBusy}
        />
        {source && (
          <p>
            Video source version {source.version} is frozen separately from these uploads. Use
            “Update video photos” to select a new source version.
          </p>
        )}
        {changed && (
          <output>
            Your uploaded photos changed. The current video still uses its saved source photos.
          </output>
        )}
        <button
          type="button"
          disabled={busy || videoBusy || !(listing.images?.length > 0)}
          onClick={snapshot}
        >
          {source ? "Update video photos" : "Use these photos"}
        </button>
        <button type="button" disabled={busy || videoBusy} onClick={load}>
          Check current draft
        </button>
        {error && <p role="alert">{error}</p>}
      </Panel>
      {source && (
        <SourceVideo
          key={`${source.uuid}:${source.version}`}
          source={source}
          listingUuid={listingUuid}
          onBusy={setVideoBusy}
          selection={selection}
          onSelectionChange={setSelection}
        />
      )}
    </>
  );
};

const CreateVideoPage = () => {
  const { listingUuid } = /** @type {{listingUuid?:string}} */ (useParams());
  const signedIn = !!getToken();
  return (
    <>
      <NewNavbar />
      <Page>
        <h1>Create a listing video</h1>
        <p>Start with photos. A listing report is optional—not a prerequisite.</p>
        {!signedIn ? (
          <p>
            <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`}>
              Sign in to create a video
            </Link>
          </p>
        ) : listingUuid ? (
          UUID.test(listingUuid) ? (
            <VideoDraft key={listingUuid} listingUuid={listingUuid} />
          ) : (
            <p role="alert">This listing link is not valid.</p>
          )
        ) : (
          <StartVideo />
        )}
      </Page>
    </>
  );
};

export default CreateVideoPage;
